import { useApolloClient } from '@apollo/client';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationParticipant } from '@provision/entity-types';
import { getFullName, getSiteName } from '@provision/formatting-utils';
import { useCallback } from 'react';
import { formHasChanges, FormInfo } from 'src/components';
import {
  canUpdateConversationUrgency,
  Conversation,
  ConversationType,
  getAddressesByClient,
  getConversationParticipantForEntity,
  mapClientToAddress,
  useAddress,
  useGroupRules,
} from 'src/graphql';
import { useConversationParticipation } from 'src/graphql/entities/conversation-participation/conversation-participation';
import { ConversationDetailsFormValues } from 'src/pages/Conversations/SidePanel/ConversationDetails/use-conversation-properties-form';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { useConversationEntity } from './conversation-entity-context';

export function useConversationPropertiesSubmit(): (
  values: ConversationDetailsFormValues,
  formInfo?: FormInfo<ConversationDetailsFormValues>,
) => Promise<void> {
  const { conversation, onUpdate } = useConversationEntity();
  const { groupRules } = useGroupRules();
  const canUpdateUrgency = canUpdateConversationUrgency(groupRules);

  const apolloClient = useApolloClient();
  const { createConversationToAddress, createClientToAddress } = useAddress();
  const { confirm } = useConfirmationModal();
  const { showSuccessAlert } = useNotifications();
  const { createParticipationWithRelatedData } = useConversationParticipation();

  const associateAddressWithClient = useCallback(
    async (
      requester: Partial<ConversationParticipant>,
      values: ConversationDetailsFormValues,
    ) => {
      // use the first address we find on the client
      const addressToUse =
        requester.client?.addresses?.[0] ??
        (
          await getAddressesByClient({
            apolloClient,
            entityId: values.requester?.id || '',
          })
        )[0];

      // if the client has an address, attach it to the conversation
      if (!addressToUse) {
        return;
      }

      await createConversationToAddress(
        conversation.conversationId,
        {
          ...addressToUse,
          fullAddress: getSiteName(addressToUse),
        },
        conversation.partnerId,
      );
    },
    [
      apolloClient,
      conversation.conversationId,
      conversation.partnerId,
      createConversationToAddress,
    ],
  );

  const takeExtraActionsOnRequesterUpdate = useCallback(
    async ({
      participantEntity,
      requester,
      values,
      conversationAddress,
    }: {
      participantEntity: ConversationParticipantEntity;
      requester: Partial<ConversationParticipant>;
      values: ConversationDetailsFormValues;
      conversationAddress: Conversation['address'];
    }) => {
      if (participantEntity !== ConversationParticipantEntity.Client || !requester?.id) {
        return;
      }

      if (!conversationAddress) {
        return associateAddressWithClient(requester, values);
      }

      const existingAddress = requester.client?.addresses?.find(
        (it) => it.addressId === conversationAddress.addressId,
      );

      if (
        !existingAddress &&
        (await confirm({
          title: 'Add client to site?',
          body: `${getFullName(requester.client)} isn't associated with this site yet.`,
          confirmLabel: 'Add client to site',
          cancelLabel: 'Not now',
        }))
      ) {
        void createClientToAddress({
          client: mapClientToAddress(requester.client as Partial<Client> | Client),
          address: conversationAddress,
          partnerId: conversation?.partner?.partnerId,
        }).then(() => {
          showSuccessAlert('Client was associated with the site');
        });
      }
    },

    [
      associateAddressWithClient,
      confirm,
      conversation?.partner?.partnerId,
      createClientToAddress,
      showSuccessAlert,
    ],
  );

  // TODO: consider refactoring this into custom mutation that will
  // handle all side effects within one api call, such as requester addresses
  // management, fetching requester's participant, creating conversation to address
  // making assignee to be a participant etc.
  return useCallback(
    async (
      values: ConversationDetailsFormValues,
      formInfo?: FormInfo<ConversationDetailsFormValues>,
    ) => {
      const { formState, info, defaultValues } = formInfo || {};

      const hasChanged = formHasChanges<ConversationDetailsFormValues>({
        values,
        formState,
        info,
        defaultValues,
      });

      if (!hasChanged) {
        return;
      }

      const participantEntity = values.requester?.participantEntity;

      let requester: Partial<ConversationParticipant> = conversation.requester;
      let hasUpdatedRequester = false;

      // Role is client so we must update conversation address
      if (
        values.requester?.id &&
        values.requester.id !== conversation.requester?.participantIdentity
      ) {
        const newRequester = await getConversationParticipantForEntity({
          apolloClient,
          partnerId: conversation.partnerId,
          entityId: values.requester.id,
          participantEntity,
        });

        hasUpdatedRequester = !!newRequester;
        requester = newRequester || requester;
      }

      const update = removeNotDefinedProperties({
        conversationStatusId: values.conversationStatus?.id,
        conversationAssigneeGroupId: values.assignee?.groupId ?? undefined,
        conversationAssigneeId: values?.assignee
          ? (values?.assignee.userId ?? null)
          : undefined,
        type: values.type?.id as ConversationType,
        urgency: canUpdateUrgency ? values.urgency?.id : undefined,
        requesterId: requester?.conversationParticipantId!,
        requester: requester as ConversationParticipant,
        requesterFullName: values.requester?.label || conversation.requesterFullName,
        associatedAddresses: conversation.associatedAddresses,
      });

      await onUpdate({ ...conversation, ...update });

      if (
        update.conversationAssigneeId &&
        conversation.conversationAssigneeId !== update.conversationAssigneeId
      ) {
        void createParticipationWithRelatedData({
          participantEntity: ConversationParticipantEntity.PlatformUser,
          participationMode: ConversationParticipationMode.Assist,
          entityIdentifier: update.conversationAssigneeId,
          channels: [
            {
              channelType: ConversationChannelType.Chat,
              externalIdentifier: update.conversationAssigneeId,
            },
          ],
          conversationId: conversation.id,
          partnerId: conversation.partnerId,
          partnerEmail:
            conversation.partner.brandGroups?.[0]?.supportEmail ??
            conversation.partner.supportEmail,
          conversation,
        });
      }
      if (hasUpdatedRequester) {
        await takeExtraActionsOnRequesterUpdate({
          values,
          participantEntity,
          requester,
          conversationAddress: conversation.address,
        });
      }
    },
    [
      createParticipationWithRelatedData,
      conversation,
      onUpdate,
      takeExtraActionsOnRequesterUpdate,
      apolloClient,
      canUpdateUrgency,
    ],
  );
}
